import { Observable } from 'rxjs';
import { IKeyValue } from '@carm/_utils/models';
import { ColDef, IDetailCellRendererParams } from '@ag-grid-community/core';
import { CarmResources, GroupTypes } from '@carm/_core';

export interface AgGridConfig {
    columnDefs: ColDef[];
    actionsHeaderData?: any;
    actions?: TableAction[];
    checkbox?: boolean;
    checkboxSelectable?: (node: any) => boolean;
    radiobutton?: boolean;
    isExternalFilterPresent?: () => boolean;
    doesExternalFilterPass?: (node: any) => boolean;
    rowModelType?: 'clientSide' | 'infinite' | 'serverSide';
    hideQuickFilter?: boolean;
    resizeAfterFirstRender?: boolean;
    masterDetail?: boolean,
    detailCellRendererParams?: IDetailCellRendererParams;
    serverSideInfiniteScroll?: true
    maxActionsColumnWidth?: number;
}

export interface ActionConfig {
  actionFieldKey: string;                  // optional reference to the field that determines dynamic display
  actionFieldValues: (string | number)[];  // optional list of values of the field determining display conditions
}
  // Interface defining the action buttons in the command column of the ag-grid
export interface  TableAction {
    actionType: ACTIONTYPES;
    showActionFieldKey?: string;                  // optional reference to the field that determines dynamic display
    showActionFieldValues?: (string | number)[];  // optional list of values of the field determining display conditions
    // function called to enable/disable an action button
    isActionEnabled?: (rowData: any) => boolean;
    isVisible?: (rowData: any) => boolean;
    buttonText?: string; // option text to change button.
    acl?: { resource: CarmResources, group: GroupTypes }[];
}

export interface PaginationModel {
  pageSize: number;
  currentPage: number;
}

export const linkTarget = {
    blank: '_blank',
    self: '_self',
    parent: '_parent',
    top: 'top'
};

// https://www.ag-grid.com/javascript-grid-internationalisation/
export const agGridDefaultLocaleFr = {
    page: 'fr-Page',
    more: 'fr-More',
    to: 'fr-To',
    of: 'fr-Of',
    next: 'fr-Next',
    last: 'fr-Last',
    first: 'fr-First',
    previous: 'fr-Previous',
    loadingOoo: 'fr-Loading...',
    selectAll: 'fr-Select All',
    searchOoo: 'fr-Search...',
    blanks: 'fr-Blanc',
    filterOoo: 'fr-Filter...',
    applyFilter: 'fr-ApplyFilter...',
    equals: 'fr-Equals',
    notEqual: 'fr-NotEqual',
    lessThan: 'fr-LessThan',
    greaterThan: 'fr-GreaterThan',
    lessThanOrEqual: 'fr-LessThanOrEqual',
    greaterThanOrEqual: 'fr-GreaterThanOrEqual',
    inRange: 'fr-InRange',
    contains: 'fr-Contains',
    notContains: 'fr-NotContains',
    startsWith: 'fr-Starts dawith',
    endsWith: 'fr-Ends dawith',
    andCondition: 'fr-AND',
    orCondition: 'fr-OR',
    group: 'fr-Group',
    columns: 'fr-Columns',
    filters: 'fr-Filters',
    rowGroupColumns: 'fr-Pivot Cols',
    rowGroupColumnsEmptyMessage: 'fr- drag cols to group',
    valueColumns: 'fr-Value Cols',
    pivotMode: 'fr-Pivot-Mode',
    groups: 'fr-Groups',
    values: 'fr-Values',
    pivots: 'fr-Pivots',
    valueColumnsEmptyMessage: 'fr- drag cols to aggregate',
    pivotColumnsEmptyMessage: 'fr- drag here to pivot',
    toolPanelButton: 'fr- tool panel',
    noRowsToShow: 'fr- no rows',
    enabled: 'fr-Enabled',
    pinColumn: 'fr-Pin Column',
    valueAggregation: 'fr-Value Agg',
    autosizeThiscolumn: 'fr-Autosize Diz',
    autosizeAllColumns: 'fr-Autsoie em All',
    groupBy: 'fr-Group by',
    ungroupBy: 'fr-UnGroup by',
    resetColumns: 'fr-Reset Those Cols',
    expandAll: 'fr-Open-em-up',
    collapseAll: 'fr-Close-em-up',
    toolPanel: 'fr-Tool Panelo',
    export: 'fr-Exporto',
    csvExport: 'fr-CSV Exportp',
    excelExport: 'fr-Excel Exporto (.xlsx)',
    excelXmlExport: 'fr-Excel Exporto (.xml)',
    pivotChartAndPivotMode: 'fr-Pivot Chart & Pivot Mode',
    pivotChart: 'fr-Pivot Chart',
    chartRange: 'fr-Chart Range',
    columnChart: 'fr-Column',
    groupedColumn: 'fr-Grouped',
    stackedColumn: 'fr-Stacked',
    normalizedColumn: 'fr-100% Stacked',
    barChart: 'fr-Bar',
    groupedBar: 'fr-Grouped',
    stackedBar: 'fr-Stacked',
    normalizedBar: 'fr-100% Stacked',
    pieChart: 'fr-Pie',
    pie: 'fr-Pie',
    doughnut: 'fr-Doughnut',
    line: 'fr-Line',
    xyChart: 'fr-X Y (Scatter)',
    scatter: 'fr-Scatter',
    bubble: 'fr-Bubble',
    areaChart: 'fr-Area',
    area: 'fr-Area',
    stackedArea: 'fr-Stacked',
    normalizedArea: 'fr-100% Stacked',
    pinLeft: 'fr-Pin &lt;&lt;',
    pinRight: 'fr-Pin &gt;&gt;',
    noPin: 'fr-DontPin &lt;&gt;',
    sum: 'fr-Sum',
    min: 'fr-Min',
    max: 'fr-Max',
    none: 'fr-None',
    count: 'fr-Count',
    average: 'fr-Average',
    filteredRows: 'fr-Filtered',
    selectedRows: 'fr-Selected',
    totalRows: 'fr-Total Rows',
    totalAndFilteredRows: 'fr-Rows',
    copy: 'fr-Copy',
    copyWithHeaders: 'fr-Copy Wit hHeaders',
    ctrlC: 'ctrl n C',
    paste: 'fr-Paste',
    ctrlV: 'ctrl n V',
    pivotChartTitle: 'fr-Pivot Chart',
    rangeChartTitle: 'fr-Range Chart',
    settings: 'fr-Settings',
    data: 'fr-Data',
    format: 'fr-Format',
    categories: 'fr-Categories',
    series: 'fr-Series',
    xyValues: 'fr-X Y Values',
    paired: 'fr-Paired Mode',
    axis: 'fr-Axis',
    color: 'fr-Color',
    thickness: 'fr-Thickness',
    xRotation: 'fr-X Rotation',
    yRotation: 'fr-Y Rotation',
    ticks: 'fr-Ticks',
    width: 'fr-Width',
    length: 'fr-Length',
    padding: 'fr-Padding',
    chart: 'fr-Chart',
    title: 'fr-Title',
    background: 'fr-Background',
    font: 'fr-Font',
    top: 'fr-Top',
    right: 'fr-Right',
    bottom: 'fr-Bottom',
    left: 'fr-Left',
    labels: 'fr-Labels',
    size: 'fr-Size',
    minSize: 'fr-Minimum Size',
    maxSize: 'fr-Maximum Size',
    legend: 'fr-Legend',
    position: 'fr-Position',
    markerSize: 'fr-Marker Size',
    markerStroke: 'fr-Marker Stroke',
    markerPadding: 'fr-Marker Padding',
    itemPaddingX: 'fr-Item Padding X',
    itemPaddingY: 'fr-Item Padding Y',
    strokeWidth: 'fr-Stroke Width',
    offset: 'fr-Offset',
    offsets: 'fr-Offsets',
    tooltips: 'fr-Tooltips',
    callout: 'fr-Callout',
    markers: 'fr-Markers',
    shadow: 'fr-Shadow',
    blur: 'fr-Blur',
    xOffset: 'fr-X Offset',
    yOffset: 'fr-Y Offset',
    lineWidth: 'fr-Line Width',
    normal: 'fr-Normal',
    bold: 'fr-Bold',
    italic: 'fr-Italic',
    boldItalic: 'fr-Bold Italic',
    predefined: 'fr-Predefined',
    fillOpacity: 'fr-Fill Opacity',
    strokeOpacity: 'fr-Line Opacity',
    columnGroup: 'fr-Column',
    barGroup: 'fr-Bar',
    pieGroup: 'fr-Pie',
    lineGroup: 'fr-Line',
    scatterGroup: 'fr-Scatter',
    areaGroup: 'fr-Area',
    groupedColumnTooltip: 'fr-Grouped',
    stackedColumnTooltip: 'fr-Stacked',
    normalizedColumnTooltip: 'fr-100% Stacked',
    groupedBarTooltip: 'fr-Grouped',
    stackedBarTooltip: 'fr-Stacked',
    normalizedBarTooltip: 'fr-100% Stacked',
    pieTooltip: 'fr-Pie',
    doughnutTooltip: 'fr-Doughnut',
    lineTooltip: 'fr-Line',
    groupedAreaTooltip: 'fr-Grouped',
    stackedAreaTooltip: 'fr-Stacked',
    normalizedAreaTooltip: 'fr-100% Stacked',
    scatterTooltip: 'fr-Scatter',
    bubbleTooltip: 'fr-Bubble',
    noDataToChart: 'fr-No data available to be charted.',
    pivotChartRequiresPivotMode: 'fr-Pivot Chart requires Pivot Mode enabled.',
};

// possible types of inrow action buttons
export enum ACTIONTYPES {
    approve = 'approve',
    delete = 'delete',
    details = 'details',
    download = 'download',
    modify = 'modify',
    add = 'add',
    edit = 'edit',
    editInvoice = 'editinvoice',
    editVendor = 'editvendor',
    form = 'form',
    documents = 'documents',
    reject = 'reject',
    remove = 'remove',
    upload = 'upload',
    payment = 'payment',
    view = 'view',
    viewInvoice = 'viewInvoice',
    viewCommodity = 'viewCommodity',
    withdraw = 'withdraw',
    select = 'select',
    cancel = 'cancel',
    appeal = 'appeal',
    letter = 'letter',
    comment = 'comment',
    submit = 'submit',
    confirmSubmit = 'confirmSubmit',
    errors = 'errors',
    pdf = 'PDF',
    deposit = 'deposit',
    addComment = 'addComment',
    viewComment = 'viewComment',
  }

export interface IHandleServerDataSource {
  defaultSortColumn: string;
  sortByColumns: IKeyValue[];
  getRows(pageIndex: number, pageSize: number, sort: string): Observable<IGetRowsResult>;
  handleError(err: any): void;
}

export interface IHandlePagination {
  pageIndex: number;
  pageSize: number;
  filterField: string;
  filterValue: string;
  sort: string;
}

export interface IGetRowsResult {
  results: any[];
  totalRecords: number;
}

export interface ITableStateUrlParams {
  currentPage: string,
  pageSize: string,
  sort?: string,
}
